/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Link as RouterLink } from 'react-router';

import { useQuery } from '@apollo/client';
import { isNil } from 'lodash';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import useRouter from 'js/lib/useRouter';
import { formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Button, Grid, Link, Skeleton, Typography2 } from '@coursera/cds-core';
import { EditIcon } from '@coursera/cds-icons';
import { Link as TrackedLink } from '@coursera/event-pulse/react';

import type {
  ProgramDraftQuery as ProgramDraftQueryData,
  ProgramDraftQueryVariables,
} from 'bundles/enterprise-admin-coursera-for-campus-basic/utils/__generated__/ProgramDraftQuery';
import { org_coursera_program_draft_ProgramDraftStatus as ProgramDraftStatus } from 'bundles/enterprise-admin-coursera-for-campus-basic/utils/__generated__/globalTypes';
import { ProgramDraftQuery } from 'bundles/enterprise-admin-coursera-for-campus-basic/utils/queries';
import { useEnterpriseAdminTrackingData } from 'bundles/enterprise-admin-hooks/hooks/useEnterpriseAdminTrackingData';
import EnterprisePermissionsRequired from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsRequired';
import {
  ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN,
  ENTERPRISE_ADMIN_PERMISSION_VERB,
} from 'bundles/enterprise-admin-roles/types';
import { TrackedButton } from 'bundles/enterprise-ui/components/Button';
import { CopyToClipboardButton } from 'bundles/enterprise-ui/components/CopyToClipboardButton';
import { formatNumber } from 'bundles/enterprise-ui/utils';
import TrackedDivV2 from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/enterprise-admin-home';

const styles = {
  container: css`
    min-height: 227px;
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: 3px;
    overflow: hidden;

    :hover {
      box-shadow: 0 2px 10px 0 var(--cds-color-grey-100);
    }
  `,
  primarySection: css`
    padding: 24px 36px;
    background: var(--cds-color-emphasis-primary-background-strong);
  `,
  secondarySection: css`
    padding: 24px 48px;
    background-color: var(--cds-color-neutral-background-primary);
  `,
  programIdBox: css`
    height: 36px;
    border: 1px solid var(--cds-color-grey-300);
    border-radius: 4px;
    padding-left: 16px;
    margin-top: 24px;
  `,
  shellContainer: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `,
};

const Metric: React.LegacyFunctionComponentWithChildren<{
  name: string;
  value?: number;
}> = ({ name, value, children }) => {
  return (
    <Grid item xs={6} sm={4}>
      <Typography2 component="h4" variant="subtitleMedium">
        {name}
      </Typography2>
      <Typography2 variant="titleSmall" component="div">
        {value !== undefined ? formatNumber(value) : <Skeleton width={32} />}
      </Typography2>
      <div
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        {children}
      </div>
    </Grid>
  );
};

const ContractsText: React.FC<{ contractCount: number }> = ({ contractCount }) => (
  <FormattedMessage
    message={_t('Used by {count} {count, plural, =0 {No contracts} =1 {contract} other {contracts}}')}
    count={formatNumber(contractCount)}
  />
);

type PropsFromCaller = {
  thirdPartyOrganization: { id: string; slug: string };
  onContractsClick?: (event: $TSFixMe) => void;
  programId: string;
  programSlug: string;
  programCreatedAt?: number;
  programName: string;
  numberOfCourses?: number;
  numberOfContracts?: number;
  numberOfLearnersInvited?: number;
  inviteLearnersLink: string;
  numberOfLearnersJoined?: number;
  viewLearnersLink: string;
  numberOfLearnersEnrolled?: number;
  viewEnrolledLearnersLink: string;
  showProgramId?: boolean;
  numberOfSkillSets?: number;
  skillSetProgramMainLink: string;
  onSendReminder: (id: string) => void;
  assignContentLink: string;
};

type Props = PropsFromCaller;

const ProgramCard: React.FC<Props> = ({
  thirdPartyOrganization,
  onContractsClick,
  programId,
  programSlug,
  programCreatedAt,
  programName,
  numberOfCourses,
  numberOfContracts,
  numberOfLearnersInvited,
  inviteLearnersLink,
  numberOfLearnersJoined,
  viewLearnersLink,
  numberOfLearnersEnrolled,
  viewEnrolledLearnersLink,
  showProgramId,
  numberOfSkillSets,
  skillSetProgramMainLink,
  onSendReminder,
  assignContentLink,
}) => {
  const router = useRouter();
  const { data: assemblerData } = useQuery<ProgramDraftQueryData, ProgramDraftQueryVariables>(ProgramDraftQuery, {
    skip: !programId,
    variables: { programId: programId! },
  });
  const shellStatus = assemblerData?.EnterpriseProgramDraftsV1Resource?.get?.status;
  const isShell = shellStatus !== ProgramDraftStatus.CONTENT_AND_MEMBERS;
  const canSendReminders =
    numberOfLearnersInvited &&
    typeof numberOfLearnersJoined !== 'undefined' &&
    numberOfLearnersInvited > numberOfLearnersJoined;
  const { enterpriseAdminAccount } = useEnterpriseAdminTrackingData();

  const coreProgramCard = (
    <>
      <Grid item container spacing={24} data-testid="core-program-card">
        <Metric name={_t('Learners invited')} value={numberOfLearnersInvited}>
          <EnterprisePermissionsRequired
            domain="PROGRAM"
            domainId={programId}
            subdomain={ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.LEARNERS}
            verb={ENTERPRISE_ADMIN_PERMISSION_VERB.MANAGE}
          >
            <>
              <TrackedLink
                href={inviteLearnersLink}
                data-e2e="program-card-invite-learners-link"
                typographyVariant="bodyPrimary"
                variant="quiet"
                trackingData={{
                  button: { name: 'enterprise_start_invite_learner_flow' },
                  enterprise: {
                    organizationSlug: thirdPartyOrganization.slug,
                    programSlug,
                  },
                  pageSection: {
                    sectionName: 'learning_programs',
                  },
                  enterpriseAdminAccount,
                }}
              >
                {_t('Invite learners')}
              </TrackedLink>
              {canSendReminders ? (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <TrackedLink
                  data-e2e="program-card-send-reminder-to-join"
                  href="#"
                  onClick={(e: React.MouseEvent): void => {
                    e.preventDefault();
                    onSendReminder(programId);
                  }}
                  typographyVariant="bodyPrimary"
                  variant="quiet"
                  trackingData={{
                    button: { name: 'enterprise_admin_send_reminder_to_join' },
                    enterprise: {
                      organizationSlug: thirdPartyOrganization.slug,
                      programSlug,
                    },
                    pageSection: {
                      sectionName: 'learning_programs',
                    },
                    enterpriseAdminAccount,
                  }}
                >
                  {_t('Send reminder to join')}
                </TrackedLink>
              ) : null}
            </>
          </EnterprisePermissionsRequired>
        </Metric>
        <Metric name={_t('Learners joined')} value={numberOfLearnersJoined}>
          <EnterprisePermissionsRequired
            domain="PROGRAM"
            domainId={programId}
            subdomain={ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.LEARNERS}
            verb={ENTERPRISE_ADMIN_PERMISSION_VERB.MANAGE}
          >
            <TrackedLink
              href={viewLearnersLink}
              data-e2e="program-card-view-learners-link"
              typographyVariant="bodyPrimary"
              variant="quiet"
              trackingData={{
                button: { name: 'enterprise_admin_view_learners' },
                enterprise: {
                  organizationSlug: thirdPartyOrganization.slug,
                  programSlug,
                },
                pageSection: {
                  sectionName: 'learning_programs',
                },
                enterpriseAdminAccount,
              }}
            >
              {_t('View learners')}
            </TrackedLink>
          </EnterprisePermissionsRequired>
        </Metric>
        <Metric name={_t('Enrolled learners')} value={numberOfLearnersEnrolled}>
          <EnterprisePermissionsRequired
            domain="PROGRAM"
            domainId={programId}
            subdomain={ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.LEARNERS}
            verb={ENTERPRISE_ADMIN_PERMISSION_VERB.MANAGE}
          >
            <TrackedLink
              href={viewEnrolledLearnersLink}
              data-e2e="program-card-view-enrolled-learners-link"
              typographyVariant="bodyPrimary"
              variant="quiet"
              trackingData={{
                button: { name: 'enterprise_admin_view_enrolled_learners' },
                enterprise: {
                  organizationSlug: thirdPartyOrganization.slug,
                  programSlug,
                },
                pageSection: {
                  sectionName: 'learning_programs',
                },
                enterpriseAdminAccount,
              }}
            >
              {_t('View enrolled learners')}
            </TrackedLink>
          </EnterprisePermissionsRequired>
          <EnterprisePermissionsRequired
            domain="PROGRAM"
            domainId={programId}
            subdomain={ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.CONTENT_ASSIGNMENT}
            verb={ENTERPRISE_ADMIN_PERMISSION_VERB.VIEW}
          >
            <TrackedDivV2 trackingName="assign_content_link">
              <TrackedLink
                href={assignContentLink}
                data-id="program-card-assign-content-link"
                typographyVariant="bodyPrimary"
                variant="quiet"
                trackingData={{
                  button: { name: 'enterprise_admin_assign_content' },
                  enterprise: {
                    organizationSlug: thirdPartyOrganization.slug,
                    programSlug,
                  },
                  pageSection: {
                    sectionName: 'learning_programs',
                  },
                  enterpriseAdminAccount,
                }}
              >
                {_t('Assign courses')}
              </TrackedLink>
            </TrackedDivV2>
          </EnterprisePermissionsRequired>
        </Metric>
      </Grid>
      {showProgramId && (
        <Grid item container justifyContent="space-between" css={styles.programIdBox} alignItems="center">
          <Typography2 component="p" variant="bodySecondary">
            {_t('ID: #{id}', { id: programId })}
          </Typography2>
          <CopyToClipboardButton size="small" textToCopy={programId}>
            {_t('Copy Program ID')}
          </CopyToClipboardButton>
        </Grid>
      )}
    </>
  );

  return (
    <Grid container css={styles.container} data-e2e={`Program_Summary~${programId}`} data-pendo-guide="ProgramCard">
      <Grid item xs={12} md={6} container direction="column" justifyContent="space-between" css={styles.primarySection}>
        <Grid item>
          {programCreatedAt && (
            <Typography2 component="p" color="invertBody">
              <FormattedMessage
                message={_t('Program created on {date}')}
                date={formatDateTimeDisplay(programCreatedAt, 'LL')}
              />
            </Typography2>
          )}
          <Typography2
            color="invertBody"
            variant="titleSmall"
            component="h3"
            css={{ wordBreak: 'break-word', hyphens: 'auto' }}
          >
            {programName}
          </Typography2>
        </Grid>
        <Grid item container alignItems="center" spacing={24}>
          {!isNil(numberOfContracts) && onContractsClick && (
            <Grid item>
              <TrackedButton
                trackingName="used_by_contracts"
                trackingData={{ thirdPartyOrganizationId: thirdPartyOrganization.id }}
                onClick={onContractsClick}
                icon={<EditIcon size="small" />}
                iconPosition="before"
                variant="ghostInvert"
                size="small"
                css={{ padding: 0 }}
              >
                <ContractsText contractCount={numberOfContracts} />
              </TrackedButton>
            </Grid>
          )}
          {!isNil(numberOfCourses) && (
            <Grid item>
              <Typography2 component="p" color="invertBody">
                <FormattedMessage
                  message={_t('{count} {count, plural, =1 {course} other {courses}}')}
                  count={formatNumber(numberOfCourses)}
                />
              </Typography2>
            </Grid>
          )}
          {!isNil(numberOfSkillSets) && (
            <Grid item>
              <EnterprisePermissionsRequired domain="PROGRAM" domainId={programId} subdomain="SKILLSETS" verb="VIEW">
                <Link
                  component={RouterLink}
                  to={skillSetProgramMainLink}
                  key={programId}
                  invert
                  style={{ color: 'var(--cds-color-neutral-primary-invert)' }} // `invert` does not work because of `.styleguide a`
                  typographyVariant="bodyPrimary"
                  data-e2e="program-card-num-of-skillsets"
                >
                  <FormattedMessage
                    message={_t('{count} {count, plural, =1 {SkillSet} other {SkillSets}}')}
                    count={formatNumber(numberOfSkillSets)}
                  />
                </Link>
              </EnterprisePermissionsRequired>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        container
        direction="column"
        justifyContent={showProgramId ? 'flex-end' : 'center'}
        css={styles.secondarySection}
      >
        {!isShell ? (
          coreProgramCard
        ) : (
          <EnterprisePermissionsRequired
            domain="PROGRAM"
            domainId={programId}
            subdomain={
              shellStatus === ProgramDraftStatus.MEMBERS_ONLY
                ? ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.CURRICULUM
                : ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.LEARNERS
            }
            verb={
              shellStatus === ProgramDraftStatus.MEMBERS_ONLY
                ? ENTERPRISE_ADMIN_PERMISSION_VERB.EDIT
                : ENTERPRISE_ADMIN_PERMISSION_VERB.MANAGE
            }
            substituteComponent={coreProgramCard}
          >
            <div css={styles.shellContainer} data-e2e="continue-program-set-up-card">
              <Typography2
                component="h4"
                variant="subtitleMedium"
                css={css`
                  margin-bottom: var(--cds-spacing-50);
                `}
              >
                {_t('Continue your program set up')}
              </Typography2>
              <Typography2
                component="p"
                variant="bodySecondary"
                css={css`
                  margin-bottom: var(--cds-spacing-200);
                `}
              >
                {_t('Continue setting up your learning program in a few steps')}
              </Typography2>
              <Button
                variant="primary"
                size="small"
                onClick={(event) => {
                  event?.stopPropagation();
                  event?.preventDefault();
                  if (shellStatus === 'CONTENT_ONLY') {
                    if (!numberOfCourses && !numberOfSkillSets) {
                      router.push(`/o/${thirdPartyOrganization.slug}/program-creation/${programSlug}/curation-options`);
                    } else {
                      router.push(
                        `/o/${thirdPartyOrganization.slug}/program-creation/${programSlug}/content-confirmation`
                      );
                    }
                  } else {
                    router.push(`/o/${thirdPartyOrganization.slug}/program-creation/${programSlug}/shell-confirmation`);
                  }
                }}
              >
                {_t('Continue program set up')}
              </Button>
            </div>
          </EnterprisePermissionsRequired>
        )}
      </Grid>
    </Grid>
  );
};

export default ProgramCard;
